import { Invoice, InvoiceListItem, COMPANY_INFO, NETWORK_INFO } from '../types/invoice';

// Mock exchange rate (in production, this would come from an API)
const ETH_EUR_RATE = 1700;

// Generate unique invoice number
export const generateInvoiceNumber = (): string => {
  const year = new Date().getFullYear();
  const random = Math.floor(Math.random() * 100000).toString().padStart(5, '0');
  return `INV-${year}-${random}`;
};

// Create invoice from purchase data
export const createInvoice = (purchaseData: {
  itemId: string;
  itemName: string;
  itemCollection: string;
  itemImage: string;
  auctionType: 'English' | 'Dutch' | 'Vickrey' | 'Candle';
  priceCrypto: number;
  currency: string;
  txHash: string;
  blockNumber: number;
  buyerAddress: string;
  buyerName?: string;
  sellerAddress: string;
  feePercentage: number;
}): Invoice => {
  const priceEur = purchaseData.priceCrypto * ETH_EUR_RATE;
  const feeAmount = purchaseData.priceCrypto * (purchaseData.feePercentage / 100);
  const totalCrypto = purchaseData.priceCrypto;
  const totalEur = priceEur;

  return {
    id: `inv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    invoiceNumber: generateInvoiceNumber(),

    // Item Info
    itemId: purchaseData.itemId,
    itemName: purchaseData.itemName,
    itemCollection: purchaseData.itemCollection,
    itemImage: purchaseData.itemImage,
    auctionType: purchaseData.auctionType,

    // Pricing
    priceCrypto: purchaseData.priceCrypto,
    currency: purchaseData.currency,
    priceEur,
    feePercentage: purchaseData.feePercentage,
    feeAmount,
    totalCrypto,
    totalEur,

    // Transaction
    txHash: purchaseData.txHash,
    blockNumber: purchaseData.blockNumber,
    network: NETWORK_INFO.name,
    chainId: NETWORK_INFO.chainId,

    // Dates
    purchaseDate: new Date(),
    invoiceDate: new Date(),

    // Buyer
    buyerAddress: purchaseData.buyerAddress,
    buyerName: purchaseData.buyerName,

    // Seller
    sellerAddress: purchaseData.sellerAddress,

    // Company
    company: COMPANY_INFO,

    // Status
    status: 'completed',
  };
};

// Format invoice for display
export const formatInvoiceDate = (date: Date): string => {
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  }).format(date);
};

// Get Polygonscan URL for transaction
export const getTxUrl = (txHash: string): string => {
  return `${NETWORK_INFO.explorerUrl}/tx/${txHash}`;
};

// Truncate address for display
export const truncateAddress = (address: string): string => {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

// Truncate tx hash for display
export const truncateTxHash = (hash: string): string => {
  return `${hash.slice(0, 10)}...${hash.slice(-8)}`;
};

// Mock: Get all invoices for a user
export const getUserInvoices = async (userAddress: string): Promise<InvoiceListItem[]> => {
  // In production, this would fetch from API/blockchain
  return [
    {
      id: 'INV-2026-00001',
      invoiceNumber: 'INV-2026-00001',
      itemName: 'Azuki #1234',
      itemImage: 'https://images.unsplash.com/photo-1634986666676-ec8fd927c23d?w=100',
      priceCrypto: 12.5,
      currency: 'ETH',
      priceEur: 21250,
      purchaseDate: new Date(Date.now() - 3600000 * 24),
      status: 'completed',
    },
    {
      id: 'INV-2026-00002',
      invoiceNumber: 'INV-2026-00002',
      itemName: 'Doodle #5678',
      itemImage: 'https://images.unsplash.com/photo-1569437061241-a848be43cc82?w=100',
      priceCrypto: 7.8,
      currency: 'ETH',
      priceEur: 13260,
      purchaseDate: new Date(Date.now() - 3600000 * 72),
      status: 'completed',
    },
    {
      id: 'INV-2026-00003',
      invoiceNumber: 'INV-2026-00003',
      itemName: 'Moonbird #2468',
      itemImage: 'https://images.unsplash.com/photo-1614728263952-84ea256f9679?w=100',
      priceCrypto: 4.2,
      currency: 'ETH',
      priceEur: 7140,
      purchaseDate: new Date(Date.now() - 3600000 * 168),
      status: 'completed',
    },
  ];
};

// Mock: Get single invoice by ID
export const getInvoiceById = async (invoiceId: string): Promise<Invoice | null> => {
  // In production, this would fetch from API/blockchain
  return {
    id: invoiceId,
    invoiceNumber: invoiceId,

    itemId: '1234',
    itemName: 'Azuki #1234',
    itemCollection: 'Azuki',
    itemImage: 'https://images.unsplash.com/photo-1634986666676-ec8fd927c23d?w=400',
    auctionType: 'English',

    priceCrypto: 12.5,
    currency: 'ETH',
    priceEur: 21250,
    feePercentage: 2.5,
    feeAmount: 0.3125,
    totalCrypto: 12.5,
    totalEur: 21250,

    txHash: '0xabcd1234567890abcdef1234567890abcdef1234567890abcdef1234567890ab',
    blockNumber: 52456789,
    network: 'Polygon',
    chainId: 137,

    purchaseDate: new Date(Date.now() - 3600000 * 24),
    invoiceDate: new Date(Date.now() - 3600000 * 24),

    buyerAddress: '0x1234567890123456789012345678901234567890',
    buyerName: 'Usuario Demo',

    sellerAddress: '0xabcdef1234567890abcdef1234567890abcdef12',

    company: COMPANY_INFO,

    status: 'completed',
  };
};

// Generate PDF invoice (placeholder - would use a library like jsPDF in production)
export const generatePDF = async (invoice: Invoice): Promise<Blob> => {
  // In production, this would generate an actual PDF
  // For now, we'll create a simple text representation
  const content = `
FACTURA
${invoice.company.legalName}
${invoice.company.cif}
${invoice.company.address}
${invoice.company.postalCode} ${invoice.company.city}, ${invoice.company.country}

Factura Nº: ${invoice.invoiceNumber}
Fecha: ${formatInvoiceDate(invoice.invoiceDate)}

COMPRADOR:
${invoice.buyerAddress}
${invoice.buyerName || 'N/A'}

DESCRIPCIÓN                    PRECIO
${invoice.itemName}            ${invoice.priceCrypto} ${invoice.currency}
(${invoice.itemCollection})    (~€${invoice.priceEur.toLocaleString()})
Subasta: ${invoice.auctionType}

Subtotal:     ${invoice.priceCrypto} ${invoice.currency}
Fee (${invoice.feePercentage}%): ${invoice.feeAmount} ${invoice.currency}
TOTAL:        ${invoice.totalCrypto} ${invoice.currency} (~€${invoice.totalEur.toLocaleString()})

TRANSACCIÓN BLOCKCHAIN:
Hash: ${invoice.txHash}
Red: ${invoice.network} (Chain ID: ${invoice.chainId})
Bloque: ${invoice.blockNumber}
Verificar: ${getTxUrl(invoice.txHash)}

Esta factura es un comprobante de transacción blockchain.
Los datos son inmutables y verificables on-chain.
  `;

  return new Blob([content], { type: 'text/plain' });
};
